import { bn } from "./bn";
import { en, type MessageKey } from "./en";

export type { MessageKey };
export type Locale = "en" | "bn";
export const LOCALES: readonly Locale[] = ["en", "bn"] as const;

const catalogs: Record<Locale, Record<MessageKey, string>> = { en, bn };

/** Narrows an arbitrary string (cookie, user setting) to a supported locale. */
export function toLocale(value: string | null | undefined): Locale {
  return value === "bn" ? "bn" : "en";
}

/** Translates `key`, substituting `{name}` placeholders. Falls back to English. */
export function translate(
  locale: Locale,
  key: MessageKey,
  vars?: Record<string, string | number>,
): string {
  const template = catalogs[locale][key] || en[key];
  if (vars === undefined) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : formatValue(locale, value);
  });
}

function formatValue(locale: Locale, value: string | number): string {
  return typeof value === "number" ? formatNumber(locale, value) : value;
}

/** Locale-aware number formatting (Bangla digits for `bn`). */
export function formatNumber(locale: Locale, value: number): string {
  return new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en", { maximumFractionDigits: 2 }).format(value);
}

/** Locale-aware date-time formatting in the viewer's time zone. */
export function formatDateTime(locale: Locale, iso: string, timeZone?: string): string {
  return new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
    ...(timeZone === undefined ? {} : { timeZone }),
  }).format(new Date(iso));
}

/** Locale-aware calendar date in Dhaka time, e.g. "15 Nov 2026". */
export function formatDate(locale: Locale, iso: string): string {
  return new Intl.DateTimeFormat(locale === "bn" ? "bn-BD" : "en-GB", { dateStyle: "medium", timeZone: "Asia/Dhaka" }).format(new Date(iso));
}

/** Short relative time, e.g. "5 minutes ago" / "৫ মিনিট আগে". */
export function formatRelative(locale: Locale, iso: string, now: Date = new Date()): string {
  const diffSec = Math.round((new Date(iso).getTime() - now.getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale === "bn" ? "bn-BD" : "en", { numeric: "auto" });
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(diffSec, "second");
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  return rtf.format(Math.round(diffSec / 86400), "day");
}

/**
 * Formats paisa as taka with South Asian digit grouping, e.g. ৳1,15,000
 * (Bangla digits for `bn`). Whole taka are shown without decimals.
 */
export function formatTaka(locale: Locale, paisa: number): string {
  const taka = paisa / 100;
  const digits = Number.isInteger(taka) ? 0 : 2;
  const n = new Intl.NumberFormat(locale === "bn" ? "bn-BD" : "en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Math.abs(taka));
  return `${taka < 0 ? "-" : ""}৳${n}`;
}
